import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  ButtonGroup,
  Circle,
  forwardRef,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@cardboard-ui/react';
import { ErrorBoundary } from 'utils/errors';
import { t } from '@lingui/macro';
import { ScreenWithBar } from 'layouts/ScreenWithBar';
import BasicBar from 'components/bars/BasicBar';
import { ApplicationCrashedIcon } from 'components/icons';
import isDevMode from 'utils/isDevMode';
import isTestMode from 'utils/isTestMode';

export const GenericErrorBoundary = forwardRef<{}, 'div'>(
  ({ children }, ref) => {
    return (
      <ErrorBoundary ref={ref} fallback={GenericErrorFallback}>
        {children}
      </ErrorBoundary>
    );
  },
);

const GenericErrorContent = ({
  resetError,
  error,
}: {
  resetError: () => void;
  error: PossibleError;
}) => {
  const refresh = () => {
    resetError();
    setTimeout(window.resetAppState, 100);
  };

  const goHome = () => {
    window.__goToHomeScreen();
    setTimeout(resetError, 100);
    setTimeout(window.resetAppState, 200);
  };

  return (
    <VStack spacing={5}>
      <Circle bg="red.300" size="90px">
        <ApplicationCrashedIcon size="3x" color="white" />
      </Circle>
      <VStack>
        <Heading fontSize="xl">{t`Sorry, something went wrong`}</Heading>
        <Text textAlign="center">
          {t`The application has crashed and we are sorry for the inconvenience.`}
        </Text>
      </VStack>
      <ButtonGroup>
        <Button onClick={refresh}>{t`Reload the app`}</Button>
        <Button onClick={goHome}>{t`Go to home page`}</Button>
      </ButtonGroup>
      <ErrorDetail error={error} />
    </VStack>
  );
};

export interface PossibleError {
  disconnected?: boolean;
  maintenance?: boolean;
  response?: { status: number; data?: any };
  message: string;
}

export const GenericErrorFallback = ({
  resetError,
  error,
  disableBar,
}: {
  resetError: () => void;
  error: PossibleError;
  disableBar?: boolean;
}) => {
  return (
    <ScreenWithBar bar={disableBar ? null : <BasicBar>{t`Error`}</BasicBar>}>
      <GenericErrorContent resetError={resetError} error={error} />
    </ScreenWithBar>
  );
};

export const GenericErrorModal = ({
  resetError,
  error,
}: {
  resetError: () => void;
  error: PossibleError;
}) => {
  const refresh = () => {
    resetError();
    setTimeout(window.resetAppState, 100);
  };

  return (
    <Modal isOpen={!!error} onClose={refresh}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{t`Error`}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <GenericErrorContent resetError={resetError} error={error} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const ErrorDetail = (error: any) => {
  const showError = isDevMode() || isTestMode();
  return (
    <Alert
      status="error"
      flexDirection="column"
      alignItems="start"
      hidden={!showError}
    >
      <AlertTitle>{t`Error message`}</AlertTitle>
      <AlertDescription>{error.message || 'No message'}</AlertDescription>
      {error.response && (
        <pre>
          Status: {error.response.status}
          {error.response.data}
        </pre>
      )}
    </Alert>
  );
};
